/**
 * 🎵 INSTRUMENT SELECTOR
 * Composant pour choisir son instrument musical
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Music, Volume2, VolumeX, X, Check } from 'lucide-react';
import smartMelody, { INSTRUMENTS, PENTATONIC_SCALE } from './SmartMelody';

export default function InstrumentSelector({ isOpen, onClose }) {
  const [selectedInstrument, setSelectedInstrument] = useState(smartMelody.getCurrentInstrument());
  const [isEnabled, setIsEnabled] = useState(smartMelody.getIsEnabled());
  const [playingInstrument, setPlayingInstrument] = useState(null);

  // Test un instrument
  const testInstrument = (instrumentId) => {
    setPlayingInstrument(instrumentId);
    
    // Joue une petite mélodie de test
    const notes = ['C4', 'E4', 'G4', 'C5'];
    notes.forEach((note, index) => {
      setTimeout(() => {
        smartMelody.playNote(PENTATONIC_SCALE[note], instrumentId);
      }, index * 150);
    });

    setTimeout(() => setPlayingInstrument(null), 700);
  };

  // Sélectionne un instrument
  const selectInstrument = (instrumentId) => {
    setSelectedInstrument(instrumentId);
    smartMelody.saveInstrumentPreference(instrumentId);
    testInstrument(instrumentId);
  };

  // Toggle activé/désactivé
  const toggleEnabled = () => {
    const newValue = !isEnabled;
    setIsEnabled(newValue);
    smartMelody.saveEnabledPreference(newValue);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-md rounded-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #1a1025 0%, #0d0615 100%)',
            boxShadow: '0 0 60px rgba(168, 85, 247, 0.3), 0 0 120px rgba(236, 72, 153, 0.2)',
          }}
        >
          {/* Header */}
          <div className="relative p-6 pb-4">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20" />
            
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-3">
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center"
                >
                  <Music className="w-6 h-6 text-white" />
                </motion.div>
                <div>
                  <h2 className="text-xl font-bold text-white">Mon Instrument</h2>
                  <p className="text-sm text-purple-300">Choisis ta signature sonore !</p>
                </div>
              </div>
              
              <button
                onClick={onClose}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Toggle On/Off */}
            <motion.button
              onClick={toggleEnabled}
              className={`mt-4 w-full p-3 rounded-xl flex items-center justify-between transition-all ${
                isEnabled 
                  ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30' 
                  : 'bg-gray-800/50 border border-gray-700'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="flex items-center gap-2 text-white">
                {isEnabled ? <Volume2 className="w-5 h-5 text-green-400" /> : <VolumeX className="w-5 h-5 text-gray-400" />}
                <span>Sons des boutons</span>
              </span>
              <div className={`w-12 h-7 rounded-full p-1 transition-colors ${isEnabled ? 'bg-green-500' : 'bg-gray-600'}`}>
                <motion.div
                  className="w-5 h-5 rounded-full bg-white shadow-lg"
                  animate={{ x: isEnabled ? 20 : 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              </div>
            </motion.button>
          </div>

          {/* Instruments Grid */}
          <div className="p-6 pt-2">
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(INSTRUMENTS).map(([id, instrument]) => {
                const isSelected = selectedInstrument === id;
                const isPlaying = playingInstrument === id;
                
                return (
                  <motion.button
                    key={id}
                    onClick={() => selectInstrument(id)}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className={`relative p-4 rounded-2xl text-left transition-all overflow-hidden ${
                      isSelected 
                        ? 'bg-gradient-to-br from-purple-500/30 to-pink-500/30 border-2 border-purple-400' 
                        : 'bg-white/5 border border-white/10 hover:border-white/20'
                    }`}
                  >
                    {/* Animation de lecture */}
                    {isPlaying && (
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-purple-500/30 to-pink-500/30"
                        animate={{ opacity: [0.5, 0, 0.5] }}
                        transition={{ duration: 0.3, repeat: 2 }}
                      />
                    )}
                    
                    {/* Checkmark si sélectionné */}
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center"
                      >
                        <Check className="w-4 h-4 text-white" />
                      </motion.div>
                    )}
                    
                    <motion.span 
                      className="text-3xl block mb-2"
                      animate={isPlaying ? { scale: [1, 1.3, 1], rotate: [0, 10, -10, 0] } : {}}
                      transition={{ duration: 0.5 }}
                    >
                      {instrument.emoji}
                    </motion.span>
                    <span className={`font-medium block ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                      {instrument.name}
                    </span>
                    
                    {/* Barres de son animées */}
                    {isPlaying && (
                      <div className="absolute bottom-2 right-2 flex items-end gap-0.5 h-4">
                        {[0, 1, 2, 3].map((i) => (
                          <motion.div
                            key={i}
                            className="w-1 bg-purple-400 rounded-full"
                            animate={{ height: ['30%', '100%', '50%', '80%', '30%'] }}
                            transition={{ duration: 0.5, delay: i * 0.1, repeat: Infinity }}
                          />
                        ))}
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 pt-0">
            <p className="text-center text-xs text-gray-500">
              🎵 Chaque bouton d'app jouera une mélodie unique avec ton instrument !
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
